import { useState } from "react";
import { ExternalLink, BadgeCheck, Copy, Check, ShieldCheck } from "lucide-react";

const VIAL_IMG = "https://customer-assets.emergentagent.com/job_dosage-research-hub/artifacts/9jd2vc4m_IMG_0352.webp";
const PRODUCT_URL = "https://bioinfinity.co/retatrutide/glp3-r";
const CODE = "RETA20";

export default function FeaturedSourceCard() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(CODE);
    } catch {
      const el = document.createElement("textarea");
      el.value = CODE;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div
      data-testid="featured-source-card"
      className="bento-card overflow-hidden"
      style={{ background: "linear-gradient(135deg, #ffffff 50%, #eef6ff 100%)" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
        {/* Vial Image */}
        <div className="md:col-span-4 flex items-center justify-center p-8 md:p-6 bg-gradient-to-br from-slate-50 to-white">
          <img
            src={VIAL_IMG}
            alt="BioInfinity Lab GLP3-R 10mg research vial"
            className="w-48 md:w-full max-w-[220px] drop-shadow-lg"
            data-testid="featured-vial-image"
            loading="eager"
            width="220"
            height="400"
            fetchPriority="high"
          />
        </div>

        {/* Product Info */}
        <div className="md:col-span-8 p-7 md:p-8 flex flex-col justify-center">
          {/* Badges row */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#113C6E]/10 rounded-full text-[11px] font-bold text-[#113C6E] tracking-wide uppercase">
              Featured Source
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-[11px] font-bold text-emerald-700">
              <BadgeCheck className="h-3 w-3" /> Verified Lab
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-full text-[11px] font-bold text-amber-700">
              20% OFF
            </span>
          </div>

          {/* Product name */}
          <h3
            data-testid="featured-product-name"
            className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mb-2"
          >
            GLP3-R <span className="text-slate-400 font-medium">(Retatrutide)</span>
          </h3>

          {/* Specs */}
          <div className="flex flex-wrap gap-x-5 gap-y-1 mb-5">
            {["10mg Vial", "99%+ Purity", "Triple-Agonist", "Research Grade"].map((spec) => (
              <span key={spec} className="flex items-center gap-1.5 text-sm text-slate-500">
                <ShieldCheck className="h-3 w-3 text-[#113C6E]" />
                {spec}
              </span>
            ))}
          </div>

          {/* Discount code */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="text-sm text-slate-500">Use code:</span>
            <button
              onClick={handleCopy}
              data-testid="featured-copy-code-btn"
              className="btn-lift inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white rounded-xl font-mono text-sm font-bold tracking-wider hover:bg-slate-800 transition-colors"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied!" : CODE}
            </button>
            <span className="text-sm font-semibold text-[#113C6E]">for 20% off</span>
          </div>

          {/* CTA */}
          <a
            href={PRODUCT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="featured-buy-btn"
            className="btn-lift inline-flex items-center gap-2 px-7 py-3.5 text-white text-sm font-bold rounded-2xl transition-colors w-fit"
            style={{ background: "#113C6E" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#0d2f59")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#113C6E")}
          >
            Buy GLP3-R Now
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
